/**
 * The concrete {@link SynthContext} recipes schedule against.
 *
 * It wraps a {@link BaseAudioContext} (live `AudioContext` OR an
 * `OfflineAudioContext` for baking) plus a single destination node — the SFX
 * bus in the live engine, or the offline context's destination when rendering.
 * A recipe only ever sees this surface, never the engine or its buses, which is
 * what makes recipes portable and bake-able.
 */
import type { OscHandle, SynthContext, WaveType } from './types.js';

/**
 * Route procedural oscillators/noise from cue recipes into a single destination
 * node. Ported from the icespace `AudioManager.osc/noise` primitives, generalized
 * so the destination (a bus GainNode) is injected rather than a global.
 */
export class BusSynthContext implements SynthContext {
  constructor(
    /** The audio clock/sample-rate source (live or offline). */
    public readonly rawContext: BaseAudioContext,
    /** Where scheduled nodes connect — the SFX bus, or an offline destination. */
    private readonly destination: AudioNode,
    /**
     * Random source for noise samples. Injectable so a bake pipeline can render
     * deterministic noise; defaults to `Math.random`. Recipes never call this
     * directly — only {@link noise} does, and it stays out of the recipe's own
     * purity contract.
     */
    private readonly random: () => number = Math.random,
  ) {}

  get sampleRate(): number {
    return this.rawContext.sampleRate;
  }

  osc(
    type: WaveType,
    freq: number,
    when: number,
    duration: number,
    volume = 0.3,
  ): OscHandle | null {
    const ctx = this.rawContext;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.value = freq;
    // Exponential fade to (near) silence — matches icespace's decay envelope.
    gain.gain.setValueAtTime(volume, when);
    gain.gain.exponentialRampToValueAtTime(0.001, when + duration);

    oscillator.connect(gain);
    gain.connect(this.destination);

    oscillator.start(when);
    oscillator.stop(when + duration);
    return { oscillator, gain };
  }

  noise(when: number, duration: number, volume = 0.3): void {
    const ctx = this.rawContext;
    const sampleRate = ctx.sampleRate;
    const length = Math.max(1, Math.floor(sampleRate * duration));
    const buffer = ctx.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      data[i] = this.random() * 2 - 1;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(volume, when);
    gain.gain.exponentialRampToValueAtTime(0.001, when + duration);

    source.connect(gain);
    gain.connect(this.destination);
    source.start(when);
    source.stop(when + duration);
  }
}
